import { getBackendUrl, resolveAssetUrl } from '../../lib/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Lock } from 'lucide-react';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [platformName, setPlatformName] = useState('Talent Partner');
  const [logoIconUrl, setLogoIconUrl] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    // Fetch platform name for title
    fetch(getBackendUrl() + '/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.platform_name) {
          setPlatformName(data.platform_name);
          document.title = `Administration | ${data.platform_name}`;
        }
        if (data.logo_icon_url) {
          setLogoIconUrl(data.logo_icon_url);
        }
      })
      .catch(err => console.error('Failed to fetch branding:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate('/admin');
      } else {
        setError(result.error || 'Échec de la connexion');
      }
    } catch (err) {
      setError('Une erreur inattendue est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-tp-bg" data-testid="admin-login-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          {logoIconUrl ? (
            <img
              src={resolveAssetUrl(logoIconUrl)}
              alt={platformName}
              className="inline-block w-20 h-20 rounded-2xl mb-4 shadow-sm object-cover"
              data-testid="login-logo-icon"
            />
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-tp-primary text-white mb-4">
              <Lock className="h-8 w-8" />
            </div>
          )}
          <h1 className="text-3xl font-display font-semibold mb-2 text-tp-text">{platformName} Admin</h1>
          <p className="text-tp-text-light">Connexion au tableau de bord</p>
        </div>

        <Card className="p-8 border-tp-border">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-tp-text">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@example.com"
                disabled={loading}
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-tp-text">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Entrez votre mot de passe"
                disabled={loading}
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-tp-primary hover:bg-tp-primary-light text-white"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoginPage;
